import React, { useState } from 'react';
import {
  Sparkles, ArrowRight, ArrowLeft, X, Check, FileText, ShieldCheck,
  Users, MessageCircle, Calendar, Award, Briefcase, HeartPulse, Building,
  Download, Printer, Signature, Scale, Eye, Fingerprint,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY = {
  name: 'Starnight Holdings Ltd',
  tag: 'Adult Entertainment Management · United Kingdom',
  logo: '/logo-transparent.png', // resolved from public/
  registeredOffice: 'United Kingdom (registered address on file with HR)',
  contact: 'hr@starnight-holdings.co.uk',
};

const CONTRACT_VERSION = 'v1.0';

/**
 * StaffWelcomeOnboarding — full-screen 4-step welcome tour + UK employment contract acceptance.
 * Overlays the staff dashboard for any account where `onboarding_completed === false`.
 * X dismisses the tour but the CONTRACT tab is required — hitting X before accepting the
 * contract just marks the tour complete, and the contract page will show again from Settings.
 */
const StaffWelcomeOnboarding = ({ currentUser, getAuthHeaders, onDone, onClose }) => {
  const [step, setStep] = useState(0);
  const [contract, setContract] = useState({
    full_legal_name: currentUser?.display_name || currentUser?.username || '',
    signature: '',
    confidentiality: false,
    conduct: false,
    gdpr: false,
  });
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const role = currentUser?.role || 'staff';

  const STEPS = [
    { id: 'welcome',  label: 'Welcome',    icon: Sparkles },
    { id: 'tour',     label: 'The Portal', icon: Eye },
    { id: 'rules',    label: 'Rules',      icon: Scale },
    { id: 'contract', label: 'Contract',   icon: FileText },
  ];

  const canAccept =
    contract.full_legal_name.trim().length > 2 &&
    contract.signature.trim().length > 1 &&
    contract.confidentiality &&
    contract.conduct &&
    contract.gdpr;

  const handleAcceptContract = async () => {
    if (!canAccept) return toast.error('Please complete every field and tick every acknowledgement');
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/account/accept-contract`, {
        contract_version: CONTRACT_VERSION,
        full_legal_name: contract.full_legal_name,
        signature: contract.signature,
        acknowledge_confidentiality: contract.confidentiality,
        acknowledge_conduct: contract.conduct,
        acknowledge_gdpr: contract.gdpr,
      }, { headers: getAuthHeaders() });
      setAccepted(true);
      toast.success('Contract signed and filed with HR');
      setTimeout(() => onDone && onDone(), 1400);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save contract');
    }
    setBusy(false);
  };

  const handleDismiss = async () => {
    // X closes the welcome tour but doesn't sign the contract.
    try {
      await axios.post(`${BACKEND_URL}/api/admin/account/complete-onboarding`, {}, { headers: getAuthHeaders() });
    } catch {}
    onClose && onClose();
  };

  const handlePrint = () => window.print();

  return (
    <div className="fixed inset-0 z-[100] min-h-screen bg-[#05050A] flex items-center justify-center px-4 py-8 overflow-y-auto" data-testid="staff-welcome-page">
      <style>{`
        @media print {
          .print-hide { display: none !important; }
          .print-contract { padding: 24px !important; background: white !important; color: black !important; }
          .print-contract * { color: black !important; background: transparent !important; border-color: #ddd !important; }
        }
      `}</style>

      <div className="fixed top-0 left-1/4 w-[700px] h-[500px] bg-[#00E5FF]/[0.05] rounded-full blur-[180px] pointer-events-none print-hide" />
      <div className="fixed bottom-0 right-1/4 w-[600px] h-[400px] bg-[#8A2BE2]/[0.05] rounded-full blur-[160px] pointer-events-none print-hide" />

      <div className="relative w-full max-w-4xl">
        {/* Close */}
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="welcome-close-btn"
          className="print-hide absolute -top-2 -right-2 z-20 w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/[0.10] flex items-center justify-center text-white/60 hover:text-white hover:border-rose-400/40 transition-all"
          title="Skip the welcome tour (you can revisit from Settings)"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Progress rail */}
        <div className="print-hide mb-6 flex items-center gap-2 justify-center">
          {STEPS.map((s, i) => {
            const active = i === step, done = i < step;
            return (
              <React.Fragment key={s.id}>
                <button
                  type="button"
                  onClick={() => setStep(i)}
                  data-testid={`welcome-step-${s.id}`}
                  className={`group flex items-center gap-2 px-3.5 py-2 rounded-full text-[10px] tracking-[0.18em] uppercase font-medium border transition-all ${
                    active ? 'border-cyan-400/40 bg-cyan-500/[0.10] text-cyan-200'
                    : done ? 'border-emerald-400/25 bg-emerald-500/[0.08] text-emerald-300 hover:border-emerald-400/50'
                    : 'border-white/[0.08] bg-white/[0.02] text-white/45 hover:text-white/70'
                  }`}
                >
                  <s.icon className="w-3 h-3" />{s.label}
                </button>
                {i < STEPS.length - 1 && <span className="w-6 h-px bg-white/[0.06]" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Main card */}
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.08] backdrop-blur-md print-contract"
          style={{
            background: 'linear-gradient(140deg, rgba(10,10,20,0.75) 0%, rgba(8,8,18,0.9) 100%)',
            boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)',
          }}>
          <div className="print-hide pointer-events-none absolute -top-24 -right-16 w-64 h-64 rounded-full blur-[120px] opacity-40 bg-cyan-500/40" />
          <div className="print-hide pointer-events-none absolute -bottom-24 -left-16 w-64 h-64 rounded-full blur-[120px] opacity-40 bg-violet-500/40" />

          {/* STEP 1 — Welcome */}
          {step === 0 && (
            <div className="relative p-8 md:p-12 text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl mb-4"
                style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.20), rgba(138,43,226,0.16))', border: '1px solid rgba(0,229,255,0.35)', boxShadow: '0 20px 50px -12px rgba(0,229,255,0.55)' }}>
                <Sparkles className="w-12 h-12 text-cyan-300" strokeWidth={1.4} />
              </div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-medium mb-2">Welcome aboard</p>
              <h1 className="text-white text-4xl md:text-5xl font-light tracking-tight leading-tight">
                Hi <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">{currentUser?.display_name || currentUser?.username}</span>
              </h1>
              <p className="text-white/60 text-sm md:text-base max-w-lg mx-auto mt-4 leading-relaxed">
                Welcome to <span className="text-white font-medium">{COMPANY.name}</span>. This is your Staff Portal — everything you need to work, chat, book leave, get paid and stay compliant lives right here.
              </p>
              <p className="text-white/40 text-xs mt-3">Take 60 seconds to get oriented ↓</p>
              <div className="mt-8 flex justify-center">
                <button type="button" onClick={() => setStep(1)} data-testid="welcome-start-tour-btn"
                  className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #00E5FF, #8A2BE2)', color: '#0a0a14', boxShadow: '0 18px 45px -12px rgba(0,229,255,0.66)' }}>
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <span className="relative">Start the tour</span>
                  <ArrowRight className="w-3.5 h-3.5 relative" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Tour */}
          {step === 1 && (
            <div className="relative p-8 md:p-10">
              <div className="text-center mb-6">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/40 font-medium mb-1">Step 2 · The Portal</p>
                <h2 className="text-white text-2xl font-light tracking-tight">What lives where</h2>
                <p className="text-white/50 text-sm mt-2">Everything is in the left sidebar — here's a taste.</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {[
                  { icon: MessageCircle, hex: '#00E5FF', label: 'Live Chat',      desc: 'Talk to customers · takeover from Nova AI' },
                  { icon: Users,         hex: '#F472B6', label: 'Staff Chat',     desc: 'Team messaging, groups, DMs' },
                  { icon: Calendar,      hex: '#10B981', label: 'Leave & Shifts', desc: 'Book leave, view rota, UK bank holidays' },
                  { icon: Briefcase,     hex: '#F59E0B', label: 'HR Inbox',       desc: 'Notices, warnings, meeting invites' },
                  { icon: Award,         hex: '#EAB308', label: 'Staff Hub',      desc: 'Games, awards, leaderboards' },
                  { icon: HeartPulse,    hex: '#F43F5E', label: 'Wellbeing',      desc: 'Mood tracking + private support' },
                ].map((t) => (
                  <div key={t.label} className="relative overflow-hidden p-3.5 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
                    <div className="pointer-events-none absolute -top-6 -right-6 w-24 h-24 rounded-full blur-[60px] opacity-40" style={{ background: t.hex }} />
                    <div className="relative">
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-2" style={{ background: `${t.hex}18`, border: `1px solid ${t.hex}35` }}>
                        <t.icon className="w-4 h-4" style={{ color: t.hex }} />
                      </div>
                      <p className="text-white text-xs font-medium">{t.label}</p>
                      <p className="text-white/50 text-[10px] mt-0.5 leading-relaxed">{t.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-3">
                <button type="button" onClick={() => setStep(0)} className="text-white/50 hover:text-white/85 text-xs tracking-[0.15em] uppercase font-medium">← Back</button>
                <button type="button" onClick={() => setStep(2)} data-testid="welcome-next-rules"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] tracking-[0.15em] uppercase font-medium hover:-translate-y-0.5 transition-all"
                  style={{ background: 'linear-gradient(135deg, #00E5FF, #8A2BE2)', color: '#0a0a14', boxShadow: '0 14px 30px -10px rgba(0,229,255,0.55)' }}>
                  Continue <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — Rules & Guidelines */}
          {step === 2 && (
            <div className="relative p-8 md:p-10">
              <div className="text-center mb-6">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/40 font-medium mb-1">Step 3 · Rules of the road</p>
                <h2 className="text-white text-2xl font-light tracking-tight">How we work together</h2>
                <p className="text-white/50 text-sm mt-2">The short version — the full contract is next.</p>
              </div>

              <div className="space-y-2.5 mb-6">
                {[
                  { hex: '#00E5FF', title: 'Confidentiality first', body: 'Every DM, HR note, subscriber record and revenue figure is confidential. Never share screenshots, subscriber details or performance data outside the team.' },
                  { hex: '#10B981', title: 'Respectful conduct',    body: 'Zero tolerance for harassment, discrimination or bullying — internally or with customers. We follow the ACAS Code of Practice for grievances and disciplinaries.' },
                  { hex: '#F59E0B', title: 'Reliable timekeeping',  body: 'Clock in and out for every shift. Give as much notice as possible for absence via HR Inbox → New Notice. Repeat unauthorised absence triggers a formal warning.' },
                  { hex: '#8A2BE2', title: 'GDPR & data care',      body: 'Only access customer or staff data you need for your role. Never export or forward personal data. Report suspected leaks immediately via Security → Report Incident.' },
                  { hex: '#F43F5E', title: 'Security hygiene',      body: 'Enable 2FA, use a unique password, never share your login. All portal actions are logged — including this contract acceptance.' },
                ].map((r) => (
                  <div key={r.title} className="relative overflow-hidden p-4 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
                    <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl" style={{ background: r.hex }} />
                    <p className="text-white text-sm font-medium">{r.title}</p>
                    <p className="text-white/55 text-xs leading-relaxed mt-1">{r.body}</p>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-3">
                <button type="button" onClick={() => setStep(1)} className="text-white/50 hover:text-white/85 text-xs tracking-[0.15em] uppercase font-medium">← Back</button>
                <button type="button" onClick={() => setStep(3)} data-testid="welcome-next-contract"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] tracking-[0.15em] uppercase font-medium hover:-translate-y-0.5 transition-all"
                  style={{ background: 'linear-gradient(135deg, #00E5FF, #8A2BE2)', color: '#0a0a14', boxShadow: '0 14px 30px -10px rgba(0,229,255,0.55)' }}>
                  Read the contract <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 4 — Contract */}
          {step === 3 && !accepted && (
            <div className="relative p-6 md:p-10">
              {/* Contract paper */}
              <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0F0F18]/70 p-6 md:p-8 mb-5 max-h-[62vh] overflow-y-auto jewel-scroll">
                <div className="flex items-start justify-between gap-4 mb-4 pb-4 border-b border-white/[0.06]">
                  <div>
                    <p className="text-[9px] tracking-[0.25em] uppercase text-white/40 font-medium">Employment Statement</p>
                    <h3 className="text-white text-lg font-medium tracking-tight">{COMPANY.name}</h3>
                    <p className="text-white/40 text-[11px] mt-0.5">{COMPANY.tag}</p>
                    <p className="text-white/35 text-[10px] mt-0.5">Contract version {CONTRACT_VERSION} · Issued {today}</p>
                  </div>
                  <img src={COMPANY.logo} alt="Starnight" className="w-16 h-16 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </div>

                <div className="text-white/75 text-[13px] leading-relaxed space-y-3">
                  <p>
                    This written statement of employment particulars is issued to <span className="text-white font-semibold">{contract.full_legal_name || currentUser?.display_name || currentUser?.username}</span> (the "Employee") by <span className="text-white font-semibold">{COMPANY.name}</span> (the "Company") in accordance with section 1 of the Employment Rights Act 1996 (UK).
                  </p>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">1. Role &amp; commencement</p>
                    <p>Your position with the Company is <span className="text-white">{currentUser?.job_title || 'Team Member'}</span> ({role}). Continuous employment starts on the date first logged into this portal.</p>
                  </div>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">2. Place of work &amp; hours</p>
                    <p>Your normal place of work is remote / hybrid unless otherwise agreed in writing. Working patterns are set out in the Leave &amp; Shifts area of the Portal. Overtime, when required, is agreed in advance.</p>
                  </div>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">3. Pay</p>
                    <p>Your rate of pay and pay reference period are recorded in the Company's Staff Directory. Payment is made in arrears by BACS to the bank account you provide. National Insurance and PAYE income tax are deducted at source.</p>
                  </div>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">4. Holiday entitlement</p>
                    <p>You are entitled to 28 days paid annual leave inclusive of UK bank holidays (pro-rata for part-time). Booking is via Leave &amp; Shifts. Untaken leave lapses at the end of each holiday year unless carried over by written agreement.</p>
                  </div>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">5. Sickness absence</p>
                    <p>Report absence via HR Inbox → New Notice (Sickness). Statutory Sick Pay is paid in accordance with UK law. Absences longer than 7 days require a Fit Note.</p>
                  </div>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">6. Notice period</p>
                    <p>During probation (first 3 months) either party may terminate with 1 week's written notice. Thereafter, statutory minimum notice applies (1 week for every full year of service, capped at 12 weeks) or 1 month, whichever is greater.</p>
                  </div>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">7. Confidentiality &amp; intellectual property</p>
                    <p>You must not disclose confidential information (customer data, revenue, screenshots, chats, HR records) during or after your employment. All work product created for the Company is owned by the Company under the Copyright, Designs and Patents Act 1988.</p>
                  </div>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">8. Data protection (UK GDPR)</p>
                    <p>You will process personal data solely under the Company's instructions and in line with the UK GDPR and the Data Protection Act 2018. Unauthorised access, export or sharing of personal data is gross misconduct.</p>
                  </div>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">9. Disciplinary &amp; grievance</p>
                    <p>The Company follows the ACAS Code of Practice on Disciplinary and Grievance procedures. All formal notices are issued and logged via the HR Inbox.</p>
                  </div>

                  <div>
                    <p className="text-cyan-300 text-xs tracking-[0.15em] uppercase font-semibold mb-1">10. Governing law</p>
                    <p>This agreement is governed by the laws of England and Wales.</p>
                  </div>
                </div>
              </div>

              {/* Sign block */}
              <div className="relative overflow-hidden rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-cyan-500/[0.05] to-violet-500/[0.05] p-5">
                <div className="grid md:grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-medium block mb-1.5">Full legal name</label>
                    <input
                      type="text"
                      value={contract.full_legal_name}
                      onChange={(e) => setContract({ ...contract, full_legal_name: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-black/40 border border-white/[0.10] rounded-xl text-white text-sm focus:border-cyan-400/50 focus:outline-none"
                      data-testid="contract-legal-name-input"
                      placeholder="As it appears on your passport / driving licence"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-medium block mb-1.5">Signature (type your name)</label>
                    <div className="relative">
                      <Signature className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cyan-300/70" />
                      <input
                        type="text"
                        value={contract.signature}
                        onChange={(e) => setContract({ ...contract, signature: e.target.value })}
                        className="w-full pl-9 pr-3.5 py-2.5 bg-black/40 border border-white/[0.10] rounded-xl text-cyan-100 text-lg italic focus:border-cyan-400/50 focus:outline-none tracking-wide"
                        style={{ fontFamily: 'cursive' }}
                        data-testid="contract-signature-input"
                        placeholder="Your name"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {[
                    { key: 'confidentiality', label: 'I have read and will comply with the confidentiality and IP terms (§7).' },
                    { key: 'conduct',         label: 'I will follow the ACAS-aligned conduct, disciplinary and grievance rules (§9).' },
                    { key: 'gdpr',            label: 'I understand my UK GDPR / Data Protection obligations (§8).' },
                  ].map(k => (
                    <label key={k.key} className="flex items-start gap-2.5 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] cursor-pointer hover:border-cyan-400/25 transition-colors">
                      <input
                        type="checkbox"
                        checked={contract[k.key]}
                        onChange={(e) => setContract({ ...contract, [k.key]: e.target.checked })}
                        className="mt-0.5 w-4 h-4 accent-cyan-400"
                        data-testid={`contract-ack-${k.key}`}
                      />
                      <span className="text-white/75 text-xs leading-relaxed">{k.label}</span>
                    </label>
                  ))}
                </div>

                <div className="flex items-center justify-between gap-3 flex-wrap print-hide">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setStep(2)} className="text-white/50 hover:text-white/85 text-xs tracking-[0.15em] uppercase font-medium">← Back</button>
                    <button type="button" onClick={handlePrint}
                      className="inline-flex items-center gap-1.5 text-white/50 hover:text-cyan-300 text-[10px] tracking-[0.15em] uppercase font-medium border border-white/[0.10] rounded-full px-3 py-1.5">
                      <Printer className="w-3 h-3" /> Print / PDF
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleAcceptContract}
                    disabled={!canAccept || busy}
                    data-testid="contract-accept-btn"
                    className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #10B981, #00E5FF)', color: '#0a0a14', boxShadow: '0 20px 45px -12px rgba(16,185,129,0.55)' }}
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    <Check className="w-3.5 h-3.5 relative" />
                    <span className="relative">{busy ? 'Signing…' : 'Accept & sign contract'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Accepted state */}
          {step === 3 && accepted && (
            <div className="relative p-12 text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-emerald-500/15 border border-emerald-500/40 mb-4">
                <ShieldCheck className="w-12 h-12 text-emerald-400" strokeWidth={1.5} />
              </div>
              <h2 className="text-white text-3xl font-light tracking-tight">Contract filed with HR</h2>
              <p className="text-white/50 text-sm mt-2">Welcome to the team. Opening your portal…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffWelcomeOnboarding;
